import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Drawer } from 'antd';
import { UserOutlined, MenuOutlined, HomeOutlined, FileTextOutlined, InfoCircleOutlined, BookOutlined } from '@ant-design/icons';
import { getBackendUrl } from '../../config/domains';
import './Navbar.css';

// 用户信息接口
interface UserInfo {
  username: string;
  storeUserName?: string;
}

type TabType = 'home' | 'case-studies' | 'about' | 'user-manual';

interface NavbarProps {
  onOpenLoginModal?: (type: 'merchant' | 'company') => void;
  userInfo?: UserInfo | null;
  onLogout?: () => void;
  activeTab?: TabType;
  onTabChange?: (tab: TabType) => void;
}

const Navbar: React.FC<NavbarProps> = ({ onOpenLoginModal, userInfo, onLogout, activeTab, onTabChange }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [navbarShadow, setNavbarShadow] = useState(false);

  // 处理滚动事件，添加/移除导航栏阴影
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setNavbarShadow(true);
      } else {
        setNavbarShadow(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMerchantLoginClick = () => {
    if (onOpenLoginModal) {
      onOpenLoginModal('merchant');
    }
    // 如果移动端菜单是展开的，则关闭它
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  const handleCompanyLoginClick = () => {
    if (onOpenLoginModal) {
      onOpenLoginModal('company');
    }
    // 如果移动端菜单是展开的，则关闭它
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  return (
    <nav className={`navbar ${navbarShadow ? 'shadow' : ''}`}>
      <div className="container navbar-container">
        <Link to="/" className="navbar-brand">
          <img src="/logo.jpg" alt="同步到家" />
        </Link>

        {/* 导航链接 - 移到中间 */}
        <div className="nav-links">
          <button
            className={`nav-link ${activeTab === 'home' ? 'active' : ''}`}
            onClick={() => {
              onTabChange?.('home');
              if (isMenuOpen) setIsMenuOpen(false);
            }}
          >
            <HomeOutlined /> 首页
          </button>
          <button
            className={`nav-link ${activeTab === 'case-studies' ? 'active' : ''}`}
            onClick={() => {
              onTabChange?.('case-studies');
              if (isMenuOpen) setIsMenuOpen(false);
            }}
          >
            <FileTextOutlined /> 装修案例
          </button>
          <button
            className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => {
              onTabChange?.('about');
              if (isMenuOpen) setIsMenuOpen(false);
            }}
          >
            <InfoCircleOutlined /> 关于我们
          </button>
          <button
            className={`nav-link ${activeTab === 'user-manual' ? 'active' : ''}`}
            onClick={() => {
              onTabChange?.('user-manual');
              if (isMenuOpen) setIsMenuOpen(false);
            }}
          >
            <BookOutlined /> 使用手册
          </button>
        </div>

        <div className="navbar-actions">
          {userInfo ? (
            // 已登录状态：显示用户名和退出按钮
            <div className="user-info">
              <span className="username">{userInfo.storeUserName || userInfo.username}</span>
              <Button
                type="text"
                href={`${getBackendUrl()}/index.php?s=/store/index/index`}
                className="enter-admin-btn"
                style={{
                  color: '#000000',
                  padding: '8px 16px',
                  borderRadius: '9999px',
                  fontSize: 'inherit'
                }}
              >
                进入后台
              </Button>
              <Button
                type="text"
                onClick={onLogout}
                className="logout-btn"
                style={{
                  color: '#000000',
                  padding: '8px 16px',
                  borderRadius: '9999px'
                }}
              >
                退出
              </Button>
            </div>
          ) : (
            // 未登录状态：显示登录按钮
            <div className="desktop-login-buttons">
              <Button
                type="text"
                onClick={handleMerchantLoginClick}
                className="merchant-login"
                icon={<UserOutlined />}
                style={{
                  color: '#1677ff',
                  padding: '8px 16px',
                  borderRadius: '9999px'
                }}
              >
                商户登录
              </Button>
              <Button
                type="text"
                onClick={handleCompanyLoginClick}
                className="merchant-login"
                icon={<UserOutlined />}
                style={{
                  color: '#1677ff',
                  padding: '8px 16px',
                  borderRadius: '9999px'
                }}
              >
                装修公司登录
              </Button>
            </div>
          )}

          {/* 移动端菜单按钮 */}
          <Button
            type="text"
            className="mobile-menu-toggle"
            icon={<MenuOutlined />}
            onClick={toggleMenu}
            style={{
              color: '#1677ff',
              fontSize: '18px',
            }}
          />

          {/* 移动端抽屉菜单 */}
          <Drawer
            placement="right"
            closable={false}
            onClose={toggleMenu}
            open={isMenuOpen}
            size={260}
            styles={{ body: { padding: 0 } }}
            style={{ paddingTop: '60px' }}
          >
            <div className="mobile-menu-content">
              {/* 移动端导航链接 */}
              <div className="mobile-nav-links">
                <button
                  className={`mobile-nav-link ${activeTab === 'home' ? 'active' : ''}`}
                  onClick={() => {
                    onTabChange?.('home');
                    toggleMenu();
                  }}
                >
                  <HomeOutlined /> 首页
                </button>
                <button
                  className={`mobile-nav-link ${activeTab === 'case-studies' ? 'active' : ''}`}
                  onClick={() => {
                    onTabChange?.('case-studies');
                    toggleMenu();
                  }}
                >
                  <FileTextOutlined /> 装修案例
                </button>
                <button
                  className={`mobile-nav-link ${activeTab === 'about' ? 'active' : ''}`}
                  onClick={() => {
                    onTabChange?.('about');
                    toggleMenu();
                  }}
                >
                  <InfoCircleOutlined /> 关于我们
                </button>
                <button
                  className={`mobile-nav-link ${activeTab === 'user-manual' ? 'active' : ''}`}
                  onClick={() => {
                    onTabChange?.('user-manual');
                    toggleMenu();
                  }}
                >
                  <BookOutlined /> 使用手册
                </button>
              </div>

              {userInfo ? (
                // 已登录状态：显示用户名和退出按钮
                <div className="mobile-user-info">
                  <div className="username">{userInfo.storeUserName || userInfo.username}</div>
                  <Button
                    type="text"
                    href={`${getBackendUrl()}/index.php?s=/store/index/index`}
                    onClick={toggleMenu}
                    className="mobile-enter-admin-btn"
                  >
                    进入后台
                  </Button>
                  <Button
                    type="text"
                    onClick={onLogout}
                    className="mobile-logout-btn"
                  >
                    退出
                  </Button>
                </div>
              ) : (
                // 未登录状态：显示登录按钮
                <>
                  <Button
                    type="text"
                    onClick={handleMerchantLoginClick}
                    className="mobile-merchant-login"
                    icon={<UserOutlined />}
                  >
                    商户登录
                  </Button>
                  <Button
                    type="text"
                    onClick={handleCompanyLoginClick}
                    className="mobile-merchant-login"
                    icon={<UserOutlined />}
                  >
                    装修公司登录
                  </Button>
                </>
              )}
            </div>
          </Drawer>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
